import { useState, useCallback } from 'react';
import Fetch from 'utils/fetch';
import { formatErrorMessage } from 'utils/handle-error';
import intl from 'react-intl-universal';
import { generateQueryParams } from 'utils/custom-functions';

export const useMaterialFormData = () => {
  const [loadingStates, setLoadingStates] = useState({
    materials: false,
    budgets: false,
    families: false,
    labeledFamilies: false,
    roundingUnities: false
  });

  const [data, setData] = useState({
    materials: [],
    budgets: [],
    families: [],
    labeledFamilies: [],
    roundingUnities: []
  });

  const [errors, setErrors] = useState({
    materials: null,
    budgets: null,
    families: null,
    labeledFamilies: null,
    roundingUnities: null
  });

  const fetchWithLoading = async (key, fetchFunction) => {
    const abortController = new AbortController();
    
    setLoadingStates(prev => ({ ...prev, [key]: true }));
    try {
      const result = await fetchFunction(abortController.signal);
      setData(prev => ({ ...prev, [key]: result }));
      setErrors(prev => ({ ...prev, [key]: null }));
      return result;
    } catch (error) {
      if (error.name !== 'AbortError') {
        const errorMessage = (error.response && error.response.status === 400)
          ? formatErrorMessage(error)
          : intl.get('stocks.timeline.feedback.serverErrorOrder');
        
        setErrors(prev => ({ ...prev, [key]: errorMessage }));
      }
      throw error;
    } finally {
      setLoadingStates(prev => ({ ...prev, [key]: false }));
    }
  };

  const fetchBudgets = useCallback(async () => {
    return fetchWithLoading('budgets', async (signal) => {
      const response = await Fetch.get('/material-groups:all', { signal });
      return response?.data;
    });
  }, []);

  const fetchFamiliesByMaterialGroup = useCallback(async (id) => {
    return fetchWithLoading('families', async (signal) => {
      const response = await Fetch.get(`/families:by-material-group?MaterialGroupId=${id}`, { signal });
      return response?.data;
    });
  }, []);

  const fetchLabeledFamiliesByFamily = useCallback(async (id) => {
    return fetchWithLoading('labeledFamilies', async (signal) => {
      const response = await Fetch.get(`/labeled-families:by-family?${generateQueryParams({ familyId: id })}`, { signal });
      return response?.data;
    });
  }, []);

  const fetchMaterials = useCallback(async () => {
    return fetchWithLoading('materials', async (signal) => {
      const response = await Fetch.get('/materials:disabled', { signal });
      return response?.data;
    });
  }, []);

  const fetchRoundingUnities = useCallback(async () => {
    return fetchWithLoading('roundingUnities', async (signal) => {
      const response = await Fetch.get('/unidadeArredondamento/obterTodasUnidadesArredondamento', { signal });
      return response?.data;
    });
  }, []);

  return {
    loadingStates,
    isLoading: Object.values(loadingStates).some(state => state),
    data,
    errors,
    actions: {
      fetchBudgets,
      fetchFamiliesByMaterialGroup,
      fetchLabeledFamiliesByFamily,
      fetchMaterials,
      fetchRoundingUnities
    }
  };
};
